import React, { useRef } from "react";
import { TourProvider } from "@reactour/tour";
import { Box, Button, Typography } from "@mui/material";
import axios from "axios";
import { useUser } from "../../../contexts/UserContext";

// firstTime vem do MySQL como 1/0 (tinyint) ou boolean, dependendo do driver
export const isPrimeiroAcesso = (user) => [true, 1, "1", "true"].includes(user?.usuario?.firstTime);

const Passo = ({ titulo, children }) => (
    <Box>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0b243d", mb: 0.75 }}>{titulo}</Typography>
        <Typography component="div" sx={{ fontSize: 13, color: "#4b5563", lineHeight: 1.55 }}>{children}</Typography>
    </Box>
);

const passos = (secretaria) => [
    {
        selector: "[data-tour='boas-vindas']",
        content: (
            <Passo titulo="Bem-vindo(a) à Clarea Vital! 👋">
                Este é o seu painel. Em poucos passos vamos mostrar onde fica cada coisa. Aqui no topo você confere o CNPJ, a razão social e o regime tributário da sua empresa.
            </Passo>
        ),
    },
    {
        selector: ".menu-toggle",
        content: (
            <Passo titulo="Menu principal">
                Clique aqui para abrir o menu e acessar todas as áreas: notas fiscais, serviços, documentos, ficha cadastral, faturas e seu perfil.
            </Passo>
        ),
    },
    {
        selector: "[data-tour='plano']",
        content: (
            <Passo titulo="Seu plano">
                Mostra o plano contratado e se ele está ativo. Em "Ver mais" você vê os detalhes da assinatura.
            </Passo>
        ),
    },
    {
        selector: "[data-tour='indicadores']",
        content: (
            <Passo titulo="Indicadores do mês">
                Acompanhe o faturamento bruto e a quantidade de notas emitidas na competência atual.
            </Passo>
        ),
    },
    {
        selector: "[data-tour='acesso-rapido']",
        content: (
            <Passo titulo="Acesso rápido">
                Atalhos para o que você mais usa: <b>Emitir NFS-e</b>, cadastrar sócios, enviar documentos e configurar seus serviços
                {secretaria ? "." : ". Em \"Secretárias\" você dá acesso à sua equipe."}
            </Passo>
        ),
    },
    {
        selector: "[data-tour='faturamento']",
        content: (
            <Passo titulo="Faturamento mensal">
                Histórico dos últimos 6 meses. A barra amarela é o mês atual, ainda em andamento.
            </Passo>
        ),
    },
    {
        selector: "[data-tour='situacao']",
        content: (
            <Passo titulo="Situação da empresa — comece por aqui">
                Para ativarmos sua empresa, complete a <b>ficha cadastral</b> e envie os documentos dos sócios. Os itens em amarelo mostram o que ainda está pendente.
            </Passo>
        ),
    },
    {
        selector: "[data-tour='notas']",
        content: (
            <Passo titulo="Notas fiscais recentes">
                As últimas notas emitidas na competência aparecem aqui. Em "Ver todas" você consulta o histórico completo.
            </Passo>
        ),
    },
    {
        selector: "[data-tour='plantao']",
        content: (
            <Passo titulo="Plantão contábil">
                Ficou com dúvida? Fale com seu contador especialista em contabilidade médica sempre que precisar.
            </Passo>
        ),
    },
];

const botaoTourSx = {
    textTransform: "none",
    fontSize: 13,
    fontWeight: 700,
    borderRadius: "8px",
    boxShadow: "none",
    px: 2,
    "&:hover": { boxShadow: "none" },
};

const estilos = {
    popover: (base) => ({ ...base, borderRadius: 12, padding: "20px 20px 16px", maxWidth: 360, boxShadow: "0 10px 30px rgba(11, 36, 61, 0.25)" }),
    maskArea: (base) => ({ ...base, rx: 12 }),
    badge: (base) => ({ ...base, background: "#0b243d", color: "#ffc845", fontWeight: 700 }),
    dot: (base, { current }) => ({ ...base, background: current ? "#ffc845" : "#e5e7eb", border: "none" }),
    close: (base) => ({ ...base, color: "#6b7280", top: 12, right: 12 }),
};

// Tour da dashboard. Abre automaticamente no primeiro acesso (usuarios.firstTime) e ao fechar marca o acesso como concluído.
const TourDashboard = ({ children }) => {
    const { user, setUser } = useUser();

    // beforeClose pode guardar uma versão antiga do callback; o ref garante o usuário atual
    const userRef = useRef(user);
    userRef.current = user;

    const concluirPrimeiroAcesso = async () => {
        if (!isPrimeiroAcesso(userRef.current)) return;

        // Atualiza local primeiro para o tour não reabrir, mesmo se a requisição falhar
        setUser((anterior) => ({ ...anterior, usuario: { ...anterior.usuario, firstTime: false } }));
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/concluirPrimeiroAcesso`, {}, {
                headers: { Authorization: localStorage.getItem("token") },
            });
        } catch (error) {
            console.error("Erro ao concluir primeiro acesso:", error);
        }
    };

    return (
        <TourProvider
            steps={passos(user?.secretaria)}
            styles={estilos}
            padding={{ mask: 8 }}
            scrollSmooth
            disableInteraction
            beforeClose={concluirPrimeiroAcesso}
            onClickMask={() => {}}
            prevButton={({ currentStep, setCurrentStep, setIsOpen }) =>
                currentStep === 0 ? (
                    <Button size="small" onClick={() => setIsOpen(false)} sx={{ ...botaoTourSx, color: "#6b7280" }}>
                        Pular
                    </Button>
                ) : (
                    <Button size="small" onClick={() => setCurrentStep((s) => s - 1)} sx={{ ...botaoTourSx, color: "#374151" }}>
                        Voltar
                    </Button>
                )
            }
            nextButton={({ currentStep, stepsLength, setCurrentStep, setIsOpen }) => {
                const ultimo = currentStep === stepsLength - 1;
                return (
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => (ultimo ? setIsOpen(false) : setCurrentStep((s) => s + 1))}
                        sx={{ ...botaoTourSx, color: "#111827", backgroundColor: "#ffc845", "&:hover": { backgroundColor: "#f5b82e", boxShadow: "none" } }}
                    >
                        {ultimo ? "Começar a usar" : "Próximo"}
                    </Button>
                );
            }}
        >
            {children}
        </TourProvider>
    );
};

export default TourDashboard;
